import type {RecipeView} from "./features/recipes/recipe-order";
import type {Potlist} from "./features/recipes/types";
import type {DeviceId} from "./models/create-id";
import type {CategoryGroupId, GroupId} from "./models/GroupRepo";

type StorageWrapper = {
  storageGet: <K extends StorageKey>(key: K) => StorageMap[K] | null;
  storageSet: <K extends StorageKey>(key: K, item: StorageMap[K]) => boolean;
  storageRemove: (key: StorageKey) => boolean;
};

export const enum StorageKey {
  LANG = "liste4000:lang",
  DEVICE_ID = "liste4000:deviceId",
  REAUTH_SECRET = "liste4000:reauthSecret",
  THEME = "liste4000:theme", // also referenced in index.html
  PILL_SPACING = "liste4000:pillSpacing",
  LAST_GROUP_ID = "liste4000:lastGroupId",
  RECIPE_VIEW = "liste4000:recipeView",

  _CONNECTION_STATE = "liste4000:connectionState:X",
  _LAST_CAT_GROUP_ID = "liste4000:lastCatGroupFor:X",
  _POTLIST = "liste4000:potListFor:X",
}

export type StorageMap = {
  [StorageKey.LANG]: "de" | "en";
  [StorageKey.DEVICE_ID]: DeviceId;
  [StorageKey.REAUTH_SECRET]: string;
  [StorageKey.THEME]: null | "dark" | "light";
  [StorageKey.PILL_SPACING]: "compact" | "wide";
  [StorageKey.LAST_GROUP_ID]: GroupId;
  [StorageKey.RECIPE_VIEW]: RecipeView;

  [StorageKey._CONNECTION_STATE]: number | null;
  [StorageKey._LAST_CAT_GROUP_ID]: CategoryGroupId;
  [StorageKey._POTLIST]: Potlist;
};

export const getStorageKey = {
  connectionState: (key: string) =>
    `liste4000:connectionState:${key}` as StorageKey._CONNECTION_STATE,
  lastCatGroupId: (groupId: GroupId) =>
    `liste4000:lastCatGroupFor:${groupId}` as StorageKey._LAST_CAT_GROUP_ID,
  potlist: (groupId: GroupId) => `liste4000:potListFor::${groupId}` as StorageKey._POTLIST,
};

const getStorage = (storageGetter: () => Storage): StorageWrapper => {
  let storage: Storage;
  try {
    storage = storageGetter();
  } catch {
    return {
      storageGet() {
        return null;
      },
      storageSet() {
        return false;
      },
      storageRemove() {
        return false;
      },
    };
  }

  return {
    storageGet(key) {
      try {
        const content = storage.getItem(key);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return content ? JSON.parse(content) : null;
      } catch {
        return null;
      }
    },

    storageSet(key, val) {
      try {
        storage.setItem(key, JSON.stringify(val));
        return true;
      } catch {
        return false;
      }
    },

    storageRemove(key) {
      try {
        storage.removeItem(key);
        return true;
      } catch {
        return false;
      }
    },
  };
};

export const storageWrapper = getStorage(() => window.localStorage);
